import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageMeta from "@/components/base/PageMeta";
import SiteLayout from "@/components/feature/SiteLayout";
import ComicRow from "@/components/feature/ComicRow";
import { allCategories, allComics, latestComics } from "@/utils/comics";

const PAGE_SIZE = 8;

const tabs = [
  { id: "all", label: "全部更新" },
  { id: "today", label: "今日更新" },
  { id: "recent", label: "近三日" },
];

export default function UpdatesPage() {
  const ordered = useMemo(() => latestComics(), []);
  const [tab, setTab] = useState("all");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const referenceDate = ordered[0]?.updatedAt.slice(0, 10) ?? "";

  const results = useMemo(() => {
    if (tab === "today") {
      return ordered.filter((comic) => comic.updatedAt.slice(0, 10) === referenceDate);
    }
    if (tab === "recent") {
      const base = new Date(referenceDate.replace(/-/g, "/")).getTime();
      return ordered.filter(
        (comic) => base - new Date(comic.updatedAt.slice(0, 10).replace(/-/g, "/")).getTime() <= 2 * 86400000
      );
    }
    return ordered;
  }, [ordered, tab, referenceDate]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [tab]);

  const visible = results.slice(0, visibleCount);
  const hasMore = visibleCount < results.length;

  return (
    <SiteLayout>
      <PageMeta
        title="最新连载更新 - JMComic漫画频道 | 每日更新速览"
        description="JMComic最新连载更新列表，汇总每日更新章节与作品动态，方便漫画爱好者快速追踪喜欢的连载漫画。"
        keywords="JMComic,连载漫画,漫画更新,最新漫画,漫画追踪"
        path="/updates"
      />

      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-7 md:px-6 md:py-9">
          <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-700">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">连载更新</span>
          </nav>
          <div className="mt-3 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-600">
                <span className="h-1.5 w-1.5 rounded-full bg-accent-500"></span>
                Daily Updates
              </span>
              <h1 className="mt-2 font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-3xl">
                最新连载更新
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
                汇总站内漫画的最近章节更新情况，按更新时间从新到旧排列，点击作品即可查看完整章节列表。
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs text-foreground-600">
                今日更新 <strong className="font-heading text-sm text-primary-700">
                  {ordered.filter((comic) => comic.updatedAt.slice(0, 10) === referenceDate).length}
                </strong> 部
              </span>
              <span className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs text-foreground-600">
                连载中 <strong className="font-heading text-sm text-foreground-950">
                  {allComics.filter((comic) => comic.status === "连载").length}
                </strong> 部
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1240px] px-4 py-6 md:px-6 md:py-8">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex rounded-full bg-background-100 p-1">
                {tabs.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setTab(item.id)}
                    className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                      tab === item.id
                        ? "bg-background-50 text-primary-700"
                        : "text-foreground-500 hover:text-foreground-800"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <span className="text-xs text-foreground-500">
                共 <strong className="font-heading text-sm text-foreground-950">{results.length}</strong> 条更新
              </span>
            </div>

            <div className="space-y-3">
              {visible.map((comic) => (
                <ComicRow key={comic.id} comic={comic} />
              ))}
            </div>

            <div className="mt-6 flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                disabled={!hasMore}
                className={`flex h-11 items-center gap-1.5 whitespace-nowrap rounded-md px-6 text-sm font-semibold transition-colors ${
                  hasMore
                    ? "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                    : "cursor-not-allowed border border-background-200 bg-background-100 text-foreground-400"
                }`}
              >
                {hasMore ? (
                  <>
                    加载更多更新
                    <i className="ri-arrow-down-line"></i>
                  </>
                ) : (
                  "已经看完全部更新"
                )}
              </button>
              <p className="text-[11px] text-foreground-400">
                已显示 {visible.length} / {results.length} 条
              </p>
            </div>
          </div>

          <aside className="space-y-4 lg:col-span-4">
            <section className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="flex items-center gap-1.5 font-heading text-sm font-bold text-foreground-950">
                <i className="ri-calendar-check-line text-base text-primary-500"></i>
                本周更新概览
              </h2>
              <ul className="mt-3 space-y-2">
                {latestComics(5).map((comic, index) => (
                  <li key={comic.id}>
                    <Link
                      to={`/detail/${comic.id}`}
                      className="flex items-center gap-2 rounded-md p-1.5 transition-colors hover:bg-background-100"
                    >
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-primary-100 font-heading text-[11px] font-bold text-primary-800">
                        {index + 1}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-xs font-semibold text-foreground-900">
                          {comic.title}
                        </span>
                        <span className="block truncate text-[10px] text-foreground-400">
                          {comic.latestChapter}
                        </span>
                      </span>
                      <span className="shrink-0 text-[10px] text-foreground-400">
                        {comic.updatedAt.slice(5, 10)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="font-heading text-sm font-bold text-foreground-950">按题材查看更新</h2>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {allCategories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/category/${category.id}`}
                    className="flex items-center gap-2 rounded-md border border-background-200 bg-background-50 px-2.5 py-2 text-xs font-medium text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className={`${category.icon} text-sm text-primary-600`}></i>
                    {category.name}
                  </Link>
                ))}
              </div>
            </section>

            <section className="rounded-lg border border-accent-200 bg-accent-50 p-4">
              <h2 className="font-heading text-sm font-bold text-accent-900">更新提醒说明</h2>
              <p className="mt-2 text-[11px] leading-relaxed text-accent-900/80">
                目前更新列表以站内演示数据生成，展示最近章节与更新时间。订阅式更新提醒将在阅读功能上线后提供。
              </p>
            </section>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}